<template>
  <Dialog
    :open="modelValue"
    as="div"
    class="relative z-50"
    @close="emit('update:modelValue', false)"
  >
    <div
      class="fixed inset-0 bg-black/30"
      aria-hidden="true"
    />
    <div class="fixed inset-0 flex items-center justify-center p-2 text-gray-700">
      <DialogPanel class="flex w-full max-w-md flex-col rounded bg-gray-50 p-3 md:p-5">
        <button
          class="mb-2 ml-auto focus:outline-none"
          @click="emit('update:modelValue', false)"
        >
          <XMarkIcon class="size-5 fill-gray-700 md:size-6" />
        </button>
        <DialogTitle class="mb-3 text-center font-serif text-xl text-gray-700 md:text-2xl">
          Editar información de dirección
        </DialogTitle>
        <form
          class="flex flex-col space-y-4"
          @submit="onSubmit"
        >
          <p class="mb-2 flex flex-col text-sm font-medium text-gray-700 md:text-base">
            <span>Dirección seleccionada:</span>
            <span class="font-semibold">{{ address.address }}</span>
            <span>{{ address.communeName }}, {{ address.regionName }}</span>
          </p>
          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-gray-700 md:text-base">Nombre de referencia</label>
            <Field
              v-model="nameReference"
              name="nameReference"
              type="text"
              class="rounded-md border border-gray-300 p-2 text-sm focus:border-lk-green focus:outline-none md:text-base"
              :class="{ 'border-red-500': errors.nameReference }"
              placeholder="Ej: Oficina principal, Bodega norte"
              :rules="{ required: true }"
            />
            <ErrorMessage
              name="nameReference"
              class="mt-1 text-xs text-red-500 md:text-sm"
            />
          </div>
          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-gray-700 md:text-base">Observaciones</label>
            <Field
              v-model="observations"
              name="observations"
              type="text"
              class="rounded-md border border-gray-300 p-2 text-sm focus:border-lk-green focus:outline-none md:text-base"
            />
          </div>
          <div class="flex flex-col">
            <label class="mb-1 text-sm font-medium text-gray-700 md:text-base">
              Horario de retiro o funcionamiento
            </label>
            <LokalDatepicker
              v-model="rangeTime"
              :time-picker="true"
              :min-time="{ hours: 8, minutes: 0 }"
              :max-time="{ hours: 20, minutes: 0 }"
              :auto-apply="true"
              :start-time="[
                { hours: 9, minutes: 0 },
                { hours: 17, minutes: 0 },
              ]"
              :range="true"
              :minutes-increment="30"
            />
            <Field
              v-model="rangeTime"
              name="rangeTime"
              label="rango horario"
              hidden
              :rules="{ required: true }"
            />
            <ErrorMessage
              name="rangeTime"
              class="mt-1 text-xs text-red-500 md:text-sm"
            />
          </div>
          <div class="flex flex-col space-y-2 pt-2">
            <lokal-loading
              v-if="isLoading"
              :loading="isLoading"
              class="self-center"
            />
            <button
              v-else
              type="submit"
              class="w-full rounded-md border border-lk-green bg-white p-2 text-sm text-lk-green hover:bg-lk-green hover:text-white md:text-base"
            >
              Guardar cambios
            </button>
            <button
              type="button"
              class="text-sm text-gray-700 hover:underline md:text-base"
              @click="emit('update:modelValue', false)"
            >
              Cancelar
            </button>
          </div>
        </form>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import { useMutation } from 'vue-query';
import addressesApi from 'api/addresses';
import useMessageStore from 'stores/message-store';
import LokalDatepicker from './shared/lokal-datepicker.vue';

interface AddressEditDialogProps {
  modelValue: boolean;
  address: Address;
}

const props = withDefaults(defineProps<AddressEditDialogProps>(), {
  modelValue: false,
});

const emit = defineEmits<{
  'update:modelValue': [boolean];
  'address-updated': [Address];
}>();

const messageStore = useMessageStore();

const nameReference = ref('');
const observations = ref('');
const rangeTime = ref(null);

const { handleSubmit, errors } = useForm();

// Helper function to format time object to string
function formatTimeObject(timeObj: { hours: number; minutes: number }) {
  if (!timeObj) return '';
  return `${timeObj.hours.toString().padStart(2, '0')}:${timeObj.minutes.toString().padStart(2, '0')}`;
}

function formatTime(time: string | null) {
  if (!time) return null;

  const [hours, minutes] = time.split(':').map(Number);
  return { hours, minutes };
}

watch(
  () => props.address,
  (newAddress) => {
    if (newAddress) {
      nameReference.value = newAddress.nameReference || '';
      observations.value = newAddress.observations || '';
      if (newAddress.initialTime || newAddress.endTime) {
        rangeTime.value = [formatTime(newAddress.initialTime), formatTime(newAddress.endTime)];
      }
    }
  },
  { immediate: true },
);

const updateAddressMutation = useMutation((data: Partial<Address>) => addressesApi.update(props.address.id, data), {
  onSuccess: (response) => {
    messageStore.showMessage('Información de dirección actualizada correctamente', 'success');
    emit('address-updated', response.data.address);
    emit('update:modelValue', false);
  },
  onError: () => {
    messageStore.showMessage('Error al actualizar la información de dirección', 'error');
  },
});

const isLoading = computed(() => updateAddressMutation.isLoading.value);
const initialTime = computed(() => {
  if (rangeTime.value && Array.isArray(rangeTime.value)) {
    return formatTimeObject(rangeTime.value[0]);
  }
  return '';
});
const endTime = computed(() => {
  if (rangeTime.value && Array.isArray(rangeTime.value)) {
    return formatTimeObject(rangeTime.value[1]);
  }
  return '';
});

const onSubmit = handleSubmit(() => {
  updateAddressMutation.mutate({
    nameReference: nameReference.value,
    initialTime: initialTime.value,
    endTime: endTime.value,
  });
});
</script>
